import { Card, DataList, ErrorView, LoadingView, StatusBadge, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Button, Label, Textbox } from '@app/engine';
import { FormatTimestamp, Logger, WrapLTR } from '@shared/common';
import { TextsClient } from '@shared/frontend';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { TextSenderKind } from '@shared/common';

/** File-local constants. */
const localConstants = {
  logContext: 'TextDetail',
  senderKindCustomer: 'customer',
  senderKindAI: 'ai',
  senderKindOwner: 'owner'
} as const;

/**
 * Conversation detail screen: the full texting transcript with the owner interject composer.
 *
 * @returns The conversation detail element.
 */
export function TextDetail() {
  const { tenantID } = useDashboard();
  const params = useParams();
  const navigate = useNavigate();
  const textID = params[Constants.Param.TextID] ?? '';
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);

  const { data, loading, error, reload } = useDashboardData((signal) => TextsClient.GetTextDetail(signal, tenantID!, textID), [tenantID, textID]);

  if (loading) return <LoadingView />;
  if (error || !data) return <ErrorView onRetry={reload} />;

  const { text, messages } = data;
  // The composer is locked when the customer opted out (the server rejects the send anyway) or while a send is in flight; an empty draft never sends.
  const composeLocked = sending || Boolean(text.optedOutAt);

  const senderLabel = (senderKind: TextSenderKind): string => {
    if (senderKind === localConstants.senderKindCustomer) return Localized.TextSenderCustomer;
    if (senderKind === localConstants.senderKindAI) return Localized.TextSenderAI;
    if (senderKind === localConstants.senderKindOwner) return Localized.TextSenderYou;
    return Localized.TextSenderSystem;
  };

  const senderClass = (senderKind: TextSenderKind): string => {
    if (senderKind === localConstants.senderKindCustomer) return Styles.turnCaller;
    if (senderKind === localConstants.senderKindAI) return Styles.turnAgent;
    if (senderKind === localConstants.senderKindOwner) return Styles.turnOwner;
    return Styles.turnSystem;
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || composeLocked) return;
    setSending(true);
    setSendError(false);
    try {
      const controller = new AbortController();
      await TextsClient.SendTextMessage(controller.signal, tenantID!, textID, body);
      setDraft('');
      reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setSendError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Button text={Localized.DashBack} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.detailBack}`} onClickHandler={() => navigate(Constants.Route.Texts)} />

      <div className={Styles.detailGrid}>
        <div className={Styles.cardStack}>
          <Card title={Localized.TextsCustomer}>
            <DataList
              rows={[
                { label: Localized.TextsCustomer, value: WrapLTR(text.customerPhoneE164) },
                { label: Localized.SharedStatus, value: <StatusBadge status={text.status} /> },
                { label: Localized.TextsLastActivity, value: FormatTimestamp(text.lastMessageAt) },
                text.optedOutAt ? { label: Localized.TextsOptedOut, hint: Localized.TextsOptedOutHint, value: FormatTimestamp(text.optedOutAt) } : null
              ]}
            />
          </Card>
        </div>

        <div className={Styles.cardStack}>
          <Card title={Localized.CallTranscript} titleHint={Localized.CallTranscriptHint}>
            {messages.length > 0 ? (
              <div className={Styles.transcript}>
                {messages.map((message) => (
                  <div key={message.id} className={`${Styles.turn} ${senderClass(message.senderKind)}`}>
                    <Label classNameContainer={Styles.turnRole} text={senderLabel(message.senderKind)} />
                    <Label text={message.body} />
                    <Label classNameContainer={Styles.turnTime} text={FormatTimestamp(message.createdAt)} />
                  </div>
                ))}
              </div>
            ) : (
              <Label classNameContainer={Styles.muted} text={Localized.TextTranscriptNone} />
            )}
            <div className={Styles.chatCompose}>
              <Textbox
                value={draft}
                onChange={setDraft}
                onConfirm={() => void send()}
                placeholder={text.optedOutAt ? Localized.TextsOptedOutHint : Localized.TextSendPlaceholder}
                classNameContainer={Styles.chatComposeInput}
                classNameText={Styles.input}
                disabled={composeLocked}
              />
              <Button
                text={sending ? Localized.TextSendBusy : Localized.TextSend}
                title={text.optedOutAt ? Localized.TextsOptedOutHint : Localized.TextSend}
                disabled={composeLocked || !draft.trim()}
                onClickHandler={() => void send()}
                classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
                classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
              />
            </div>
            {sendError ? <Label classNameContainer={Styles.muted} text={Localized.TextSendFailed} /> : null}
          </Card>
        </div>
      </div>
    </div>
  );
}
